/*
  Which queries an event makes stale (BUILD-PLAN 2.11.4).

  One table: event type -> the endpoint names of 2.9 whose data it changes. Invalidating a name marks
  every query of that name stale and refetches the ones a screen is showing; the others fetch again
  when they are next opened. The query key of an endpoint starts with its name (`["item_page", {id}]`).

  The table lists every event type of 2.5. A test compares it with the back end's list of event types,
  so a new event type cannot be forgotten. A type this table does not know still refreshes the feeds
  (DEFAULT_INVALIDATION), so a screen never goes quiet because of a newer server.
*/

import type { QueryName } from "../api/routes";

type Names = readonly QueryName[];

/** What every change to the ontology's content touches. */
const ACTIVITY: Names = ["home", "live_feed", "history_feed", "whats_new", "counters"];

/** An element or a line changed: every view that shows elements. */
const ELEMENT: Names = [
  ...ACTIVITY,
  "element",
  "item_snapshot",
  "item_page",
  "item_parts",
  "list_elements",
  "family_tree",
  "progress",
  "find_in_ontology",
  "inbox",
  "sme_card",
  "back_lane",
  "checks_list",
  "session_cards",
  "session",
  // rebuild 4.0 and 4.3: the graph and impact of any element, and the Shared parts shelf
  "element_graph",
  "element_impact",
  "shelf",
  "part_kind_view",
  // rebuild 4.4: the Capabilities list and view (steps 3 to 8, the fields it brings and its takers are read from the data)
  "capabilities",
  "capability_view",
  // rebuild 4.2: the kinds of deliverable and the lines of a sheet (what fills each line, its state, the counts)
  "deliverable_kinds",
  "deliverable_lines",
];

/** A change that waits for the SME or comes back from the SME. */
const REVIEW: Names = [...ELEMENT, "list_hand_overs", "hand_over", "team"];

// "team" is who is working now (the Overview).
const SESSION: Names = ["session", "list_sessions", "session_log", "home", "team"];

// A card that is proposed or settled changes what is proposed, so the progress and the tree percentages move with it.
const CARDS: Names = [...ACTIVITY, "session_cards", "session", "item_snapshot", "element", "list_hand_overs", "progress", "family_tree", "proposals_waiting", "capabilities", "capability_view", "card", "deliverable_kinds", "deliverable_lines"];

// The spots a reading settles (not data, parked for another item, left to another discipline) are lines of the kind's sheet too.
const DELIVERABLE: Names = ["deliverable", "list_deliverables", "spots", "item_backlog", "session", "home", "deliverable_lines"];

const NOTE: Names = ["element", "sme_card", "inbox", "back_lane", "counters", "home", "live_feed", "history_feed"];

const LOCK: Names = ["element", "session_cards", "item_snapshot"];

const FILES: Names = ["files_view", "file_preview", "home"];

const ADMIN_PEOPLE: Names = ["admin_people", "admin_workspaces", "me"];

export const INVALIDATIONS: Readonly<Record<string, Names>> = {
  // 2.5.3 Starter pack
  starter_pack_loaded: ELEMENT,
  element_loaded: ELEMENT,
  line_loaded: ELEMENT,

  // 2.5.4 Sessions
  session_started: [...SESSION, "item_backlog"],
  scope_set: [...SESSION, "item_snapshot"],
  aim_set: SESSION,
  plan_shown: SESSION,
  plan_go: SESSION,
  plan_changed: SESSION,
  plan_paused: SESSION,
  step_entered: SESSION,
  workflow_opened: SESSION,
  workflow_closed: SESSION,
  session_paused: SESSION,
  session_resumed: SESSION,
  session_finished: SESSION,
  sweep_done: ["session", "session_log"],

  // 2.5.5 Intake
  deliverable_uploaded: DELIVERABLE,
  deliverable_refused: ["list_deliverables"],
  deliverable_attached: DELIVERABLE,
  spots_marked_stale: DELIVERABLE,
  intake_started: DELIVERABLE,
  intake_confirmed: DELIVERABLE,
  intake_page_done: DELIVERABLE,
  intake_cancelled: DELIVERABLE,
  deliverable_read: DELIVERABLE,
  spot_added_by_hand: ["spots", "deliverable"],
  spot_corrected: ["spots", "deliverable_lines"],
  spot_scope_set: ["spots", "deliverable", "deliverable_lines"],
  spot_not_data_set: ["spots", "deliverable", "deliverable_lines"],
  spot_home_proposed: ["spots"],
  spot_coverage_set: ["spots", "deliverable", "deliverable_lines"],
  spot_parked: ["spots", "item_backlog", "home", "item_snapshot", "deliverable_lines"],
  parked_spot_taken: ["spots", "item_backlog", "home", "item_snapshot", "session", "deliverable_lines"],

  // 2.5.6 Agent (the words themselves come over the socket; the stored conversation is read again at the end of a turn)
  agent_turn_started: ["conversation", "session_log"],
  agent_turn_ended: ["conversation", "session_log"],
  skill_topic_opened: [],
  questions_asked: ["conversation"],
  answer_given: ["conversation", "session_log"],
  explanation_given: ["conversation"],
  log_note_written: ["session_log"],
  point_to_shown: ["conversation"],

  // 2.5.7 Cards and batches
  card_proposed: CARDS,
  card_proposed_again: CARDS,
  card_waiting_for_lock: ["session_cards", "session", "card"],
  card_out_of_date: ["session_cards", "session", "item_snapshot", "proposals_waiting", "card"],
  card_accepted: [...CARDS, ...ELEMENT],
  card_changed_and_accepted: [...CARDS, ...ELEMENT],
  card_declined: [...CARDS, "spots", "item_backlog"],
  card_ask_why: ["session_cards", "conversation"],
  no_home_yet_settled: [...CARDS, "spots", "item_backlog"],
  card_closed: CARDS,
  batch_opened: ["session", "session_cards"],
  batch_closed: ["session", "session_cards", "home"],

  // 2.5.8 Element transitions
  element_added_by_hand: REVIEW,
  element_accepted: REVIEW,
  element_validated: REVIEW,
  element_sent_back: REVIEW,
  element_rejected: REVIEW,
  element_reworked: REVIEW,
  element_reproposed: REVIEW,
  element_withdrawn: REVIEW,
  next_version_made: REVIEW,
  next_version_dropped: REVIEW,
  removal_marked: REVIEW,
  removal_validated: REVIEW,
  removal_refused_or_lifted: REVIEW,
  element_brought_back: REVIEW,
  code_corrected: REVIEW,
  element_edited: REVIEW,
  not_used_by_us_recorded: ["element", "item_page", "sme_card"],
  same_item_as_set: ["element", "item_page", "sme_card"],
  // completion round: "add to it" (UI-11) and a duplicate folded (CR-02)
  next_version_added_to: REVIEW,
  duplicate_folded: ["element", "item_page", "history_feed", "live_feed"],

  // 2.5.9 Lines
  asset_uploaded: ["symbol_set", "item_page", "item_snapshot", "element"],
  line_added: REVIEW,
  line_edited: REVIEW,
  line_validated: REVIEW,
  line_sent_back: REVIEW,
  line_rejected: REVIEW,
  line_reworked: REVIEW,
  line_withdrawn: REVIEW,
  line_next_version_made: REVIEW,
  line_next_version_dropped: REVIEW,
  line_removal_marked: REVIEW,
  line_removal_validated: REVIEW,
  line_removal_refused_or_lifted: REVIEW,
  derived_line_set: ["element", "item_page", "item_snapshot", "sme_card"],
  derived_line_removed: ["element", "item_page", "item_snapshot", "sme_card"],

  // 2.5.10 Notes, locks, restore, hand-over, chapters
  sme_note_added: NOTE,
  engineer_note_added: NOTE,
  card_comment_added: ["session_cards", ...NOTE],
  lock_taken: LOCK,
  lock_release_asked: LOCK,
  lock_released: LOCK,
  restore_point_made: ["restore_points", "session"],
  restore_started: [],
  restore_done: [...REVIEW, "restore_points", "preview_restore", "list_chapters", "chapter", "project_lists"],
  hand_over_prepared: ["hand_over", "list_hand_overs", "session"],
  hand_over_edited: ["hand_over", "list_hand_overs"],
  hand_over_sent: ["hand_over", "list_hand_overs", "inbox", "home", "back_lane", "counters", "live_feed", "team"],
  card_displayed: ["sme_card", "inbox"],
  chapter_text_edited: ["chapter", "list_chapters", "session_cards"],
  chapter_section_settled: ["chapter", "list_chapters", "session_cards", "item_page"],
  chapter_completed: [...REVIEW, "chapter", "list_chapters"],

  // 2.5.11 Open points, parts, files, checks
  open_point_logged: ["open_points", "back_lane"],
  open_point_closed: ["open_points", "back_lane"],
  open_point_reopened: ["open_points", "back_lane"],
  part_marked_none_needed: ["item_parts", "item_snapshot", "item_page"],
  part_mark_cleared: ["item_parts", "item_snapshot", "item_page"],
  // rebuild stage 2: the project's target list changes what is needed at once; the element, card and check events above and below
  // move `progress` and `family_tree` as well (package 2G).
  target_list_set: ["progress", "family_tree", "list_deliverables", "deliverable", "item_page", "home", "deliverable_kinds", "deliverable_lines"],
  // rebuild 4.4: step 1 or 2 of a capability recorded (the steps 3 to 8 move with the element and card events)
  capability_step: ["capabilities", "capability_view"],
  files_write_requested: ["files_view"],
  files_rebuilt: FILES,
  files_check_failed: FILES,
  // a check error holds an element back from "Send N", so the progress reads again
  checks_run: ["checks_list", "counters", "progress", "family_tree"],
  coverage_run: ["coverage_report", "latest_coverage", "deliverable", "spots", "session", "deliverable_lines"],

  // 2.5.12 Memory, notes, practice, visits, project lists
  memory_updated: ["my_memory", "onboarding", "onboarding_slides"],
  memory_cleared: ["my_memory", "onboarding", "onboarding_slides"], // a cleared memory forgets where Getting started stopped
  discipline_note_offered: ["discipline_notes"],
  discipline_note_written: ["discipline_notes"],
  discipline_note_changed: ["discipline_notes"],
  discipline_note_removed: ["discipline_notes"],
  discipline_note_dropped: ["discipline_notes"],
  onboarding_progress: ["onboarding", "onboarding_slides", "my_memory"],
  practice_started: ["onboarding", "onboarding_slides", "me"],
  practice_wiped: ["onboarding", "onboarding_slides", "me"],
  visit_ended: ["whats_new", "home"],
  project_code_list_loaded: ["project_lists", "item_page"],
  project_code_list_replaced: ["project_lists", "item_page"],
  password_reset_noted: [],

  // 2.5.13 App-level log (reaches the browser only if the server relays it to the admin page)
  workspace_provisioned: ["admin_workspaces", "me"],
  workspace_closed: ["admin_workspaces", "me"],
  workspace_restored: ["admin_workspaces", "me"],
  // rebuild stage 2 (2A): the registry of disciplines
  discipline_added: ["disciplines", "admin_disciplines"],
  discipline_closed: ["disciplines", "admin_disciplines"],
  setting_changed: [],
  account_created: ADMIN_PEOPLE,
  membership_granted: ADMIN_PEOPLE,
  role_changed: ADMIN_PEOPLE,
  account_disabled: ADMIN_PEOPLE,
  account_enabled: ADMIN_PEOPLE,
  first_password_set: ["admin_people"],
  password_reset_by_admin: ["admin_people"],
  password_changed: ["admin_people"],
  signed_in: ["admin_people"],
  sign_in_failed: [],
  signed_out: ["admin_people"],
  model_connection_set: ["model_connection", "health", "me"],
  model_connection_removed: ["model_connection", "health", "me"],
  model_connection_tested: ["model_connection"],
  personal_key_set: ["me", "admin_people"],
  personal_key_removed: ["me", "admin_people"],
  admin_memory_updated: ["admin_memory", "onboarding_slides"],
};

/** For a type this table does not know: the feeds still move. */
export const DEFAULT_INVALIDATION: Names = ACTIVITY;

/** The endpoint names an event of this type makes stale, without repeats. */
export function invalidationsFor(type: string): readonly QueryName[] {
  const names = INVALIDATIONS[type] ?? DEFAULT_INVALIDATION;
  return Array.from(new Set(names));
}

/** What a personal notice (`notice` message of 2.10) makes stale. */
export const NOTICE_INVALIDATIONS: Readonly<Record<string, Names>> = {
  sent_back_to_you: ["back_lane", "home", "counters", "element", "session_cards"],
  note_for_you: ["back_lane", "element", "sme_card", "home"],
  your_lock_released: ["element", "session_cards"],
  lock_release_asked: ["element"],
  restore_done: [...REVIEW, "restore_points", "list_chapters"],
  memory_changed: ["my_memory", "onboarding", "onboarding_slides"],
  hand_over_arrived: ["inbox", "home", "list_hand_overs", "counters"],
  intake_done: ["deliverable", "list_deliverables", "spots", "item_backlog", "home"],
};
